import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  TextField,
  FormControl,
  Grid,
  Typography,
  Alert,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
import RequiredLabel from "../../shared/RequiredLabel";

export default function BasicInfoForm({ formData, onChange }) {
  // バリデーション表示の状態
  const [showValidation, setShowValidation] = useState(false);

  // イニシャル2つだけローカル状態で管理
  const [first, setFirst] = useState("");
  const [second, setSecond] = useState("");

  // 最新の onChange を保持
  const onChangeRef = useRef(onChange);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // 初期値だけformDataからセット（編集用）
  useEffect(() => {
    if (formData.name) {
      const clean = formData.name.replace("・", "").replace(".", "");
      if (clean.length === 2) {
        setFirst(clean[0]);
        setSecond(clean[1]);
      } else {
        setFirst("");
        setSecond("");
      }
    }
  }, [formData.name]);

  // first/second が変わった時だけ親へ同期
  useEffect(() => {
    onChangeRef.current({
      name_first: first || "",
      name_second: second || "",
      name: !first || !second ? "" : `${first}・${second}`,
    });
  }, [first, second]);

  // 親コンポーネントからのバリデーション表示トリガーを検知
  useEffect(() => {
    if (formData.validationTriggered) {
      setShowValidation(true);
      // バリデーションエラーが表示された時に画面上部へスクロール
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      setShowValidation(false);
    }
  }, [formData.validationTriggered]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    // 値が変更されたらバリデーションをオフに
    setShowValidation(false);
    onChangeRef.current({ [name]: value, validationTriggered: false });
  };

  // 必須項目のバリデーションチェック
  const nameError = showValidation && (!first || !second);
  const fullnameError =
    showValidation &&
    formData.role === "employee" &&
    (!formData.full_name || formData.full_name.trim() === "");
  const birthdayError =
    showValidation && (!formData.birthday || formData.birthday.trim() === "");
  const nationalityError =
    showValidation &&
    (!formData.nationality || formData.nationality.trim() === "");

  // バリデーションエラーメッセージの生成
  const validationErrors = [];
  if (nameError) validationErrors.push("イニシャルは入力必須です。");
  if (fullnameError) validationErrors.push("氏名は入力必須です。");
  if (birthdayError) validationErrors.push("生年月日は入力必須です。");
  if (nationalityError) validationErrors.push("国籍は入力必須です。");

  return (
    <Box>
      <Typography
        variant="h5"
        gutterBottom
        fontWeight="bold"
        sx={{ marginBottom: "24px" }}
      >
        基本情報
      </Typography>

      {/* バリデーションエラーメッセージの表示 */}
      {showValidation && validationErrors.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {validationErrors.map((error, index) => (
            <div key={index}>{error}</div>
          ))}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <Typography>イニシャル</Typography>
              <RequiredLabel show={true} />
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <TextField
                value={first}
                onChange={(e) => setFirst(e.target.value.slice(0, 1))}
                inputProps={{ maxLength: 1 }}
                error={nameError}
                sx={{ width: 56 }}
                placeholder="A"
              />
              <Typography>・</Typography>
              <TextField
                value={second}
                onChange={(e) => setSecond(e.target.value.slice(0, 1))}
                inputProps={{ maxLength: 1 }}
                error={nameError}
                sx={{ width: 56 }}
                placeholder="A"
              />
            </Box>
            <Typography variant="caption" color="text.secondary">
              例：A・A（各1文字ずつ）
            </Typography>
          </Box>
        </Grid>

        {/* 従業員の場合、表示 */}
        {formData.role === "employee" && (
          <Grid item xs={12}>
            <Box>
              <Box
                sx={{ display: "flex", flexDirection: "column", width: "100%" }}
              >
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
                >
                  <Typography>氏名</Typography>
                  <RequiredLabel show={true} />
                </Box>
                <TextField
                  required
                  fullWidth
                  id="full_name"
                  name="full_name"
                  placeholder="山田太郎"
                  value={formData.full_name || ""}
                  onChange={handleChange}
                  variant="outlined"
                  error={fullnameError}
                />
              </Box>
              <Typography variant="caption" color="text.secondary">
                フルネームを入力してください
              </Typography>
            </Box>
          </Grid>
        )}

        <Grid item xs={12}>
          <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <Typography>生年月日</Typography>
              <RequiredLabel show={true} />
            </Box>
            <TextField
              required
              fullWidth
              id="birthday"
              name="birthday"
              type="date"
              value={formData.birthday || ""}
              onChange={handleChange}
              variant="outlined"
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                max: new Date().toISOString().split("T")[0],
              }}
              error={birthdayError}
            />
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
            <Typography sx={{ mb: 1 }}>性別</Typography>
            <FormControl>
              <RadioGroup
                row
                id="gender"
                name="gender"
                value={formData.gender || ""}
                onChange={handleChange}
              >
                <FormControlLabel
                  value="male"
                  control={<Radio />}
                  label="男性"
                />
                <FormControlLabel
                  value="female"
                  control={<Radio />}
                  label="女性"
                />
                <FormControlLabel
                  value="other"
                  control={<Radio />}
                  label="その他"
                />
              </RadioGroup>
            </FormControl>
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <Typography>国籍</Typography>
              <RequiredLabel show={true} />
            </Box>
            <TextField
              required
              fullWidth
              id="nationality"
              name="nationality"
              value={formData.nationality || ""}
              onChange={handleChange}
              placeholder="日本"
              variant="outlined"
              error={nationalityError}
            />
          </Box>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
            <Typography sx={{ mb: 1 }}>最寄り駅（路線）</Typography>
            <TextField
              fullWidth
              id="nearest_station_line"
              name="nearest_station_line"
              value={formData.nearest_station_line || ""}
              onChange={handleChange}
              placeholder="JR"
              variant="outlined"
            />
          </Box>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
            <Typography sx={{ mb: 1 }}>最寄り駅（駅名）</Typography>
            <TextField
              fullWidth
              id="nearest_station"
              name="nearest_station"
              value={formData.nearest_station || ""}
              onChange={handleChange}
              placeholder="東京"
              variant="outlined"
            />
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
            <Typography sx={{ mb: 1 }}>最終学歴</Typography>
            <TextField
              fullWidth
              id="final_academic_background"
              name="final_academic_background"
              value={formData.final_academic_background || ""}
              onChange={handleChange}
              placeholder="○○大学 ○○学部 ○○学科"
              variant="outlined"
            />
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}
